// src/pages/MyProvidersPage.jsx
// Upload form, validation report, list management, and summary dashboard
// for Pro/Enterprise users' provider lists.

import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/react";
import { useSubscription } from "../context/SubscriptionContext";
import { apiFetch } from "../utils/apiClient";
import AuditThresholdsPopup from "../components/AuditThresholdsPopup";
import { useMyProviders } from "../context/MyProvidersContext";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const ROW_LIMIT = 10000;

// ── Filter List Panel ─────────────────────────────────────────────────────────
function FilterListPanel({ selectedListIds, selectedLists, userId, onFilterSaved, onListLimitReached }) {
  const [open, setOpen] = useState(false);
  const [minPayments, setMinPayments] = useState("");
  const [maxPayments, setMaxPayments] = useState("");
  const [minOwnership, setMinOwnership] = useState("");
  const [maxOwnership, setMaxOwnership] = useState("");
  const [minResearch, setMinResearch] = useState("");
  const [maxResearch, setMaxResearch] = useState("");
  const [latestYearOnly, setLatestYearOnly] = useState(false);
  const [hasOwnership, setHasOwnership] = useState(null);   // null | true | false
  const [hasResearch, setHasResearch] = useState(null);
  const [isExcluded, setIsExcluded] = useState(null);
  const [hasDualInterest, setHasDualInterest] = useState(null);
  const [hasPodRisk, setHasPodRisk] = useState(null);
  const [hasAnyPayments, setHasAnyPayments] = useState(null);
  const [newListName, setNewListName] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [exportingFiltered, setExportingFiltered] = useState(false);

  function buildCriteria() {
    return {
      minPayments: parseFloat(minPayments) || 0,
      maxPayments: parseFloat(maxPayments) || 0,
      minOwnership: parseFloat(minOwnership) || 0,
      maxOwnership: parseFloat(maxOwnership) || 0,
      minResearch: parseFloat(minResearch) || 0,
      maxResearch: parseFloat(maxResearch) || 0,
      latestYearOnly,
      hasOwnership: hasOwnership,
      hasResearch: hasResearch,
      isExcluded: isExcluded,
      hasDualInterest: hasDualInterest,
      hasPodRisk: hasPodRisk,
      hasAnyPayments: hasAnyPayments,
    };
  }

  function hasAnyCriteria() {
    return (
      (parseFloat(minPayments) || 0) > 0 ||
      (parseFloat(maxPayments) || 0) > 0 ||
      (parseFloat(minOwnership) || 0) > 0 ||
      (parseFloat(maxOwnership) || 0) > 0 ||
      (parseFloat(minResearch) || 0) > 0 ||
      (parseFloat(maxResearch) || 0) > 0 ||
      latestYearOnly ||
      hasOwnership !== null ||
      hasResearch !== null ||
      isExcluded !== null ||
      hasDualInterest !== null ||
      hasPodRisk !== null ||
      hasAnyPayments !== null
    );
  }

  async function runPreview() {
    if (!hasAnyCriteria()) return;
    setPreviewing(true);
    setPreview(null);
    setSaveError(null);
    try {
      const res = await apiFetch("/api/filter-provider-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          listIds: [...selectedListIds],
          criteria: buildCriteria(),
          previewOnly: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSaveError(data.error || "Preview failed."); return; }
      setPreview(data);
      // Auto-suggest list name
      if (!newListName) {
        const parts = [];
        if (parseFloat(minPayments) > 0) parts.push(`>$${Number(minPayments).toLocaleString()}`);
        if (hasAnyPayments === true) parts.push("has payments");
        if (hasOwnership === true) parts.push("ownership");
        if (hasDualInterest === true) parts.push("dual/triple interest");
        if (isExcluded === true) parts.push("excluded");
        const suffix = parts.length > 0 ? ` · ${parts.join(", ")}` : " · filtered";
        const baseName = selectedLists.length === 1 ? selectedLists[0].list_name : `${selectedLists.length} lists combined`;
        setNewListName(`${baseName}${suffix}`.slice(0, 60));
      }
    } catch {
      setSaveError("Network error. Please try again.");
    } finally {
      setPreviewing(false);
    }
  }

  async function saveFilteredList() {
    if (!preview || !newListName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch("/api/filter-provider-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          listIds: [...selectedListIds],
          newListName: newListName.trim(),
          criteria: buildCriteria(),
          previewOnly: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error?.includes("maximum of 5")) onListLimitReached?.();
        setSaveError(data.error || "Save failed.");
        return;
      }
      setSaveSuccess(data);
      onFilterSaved?.();
    } catch {
      setSaveError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function exportFilteredProviders() {
    if (!preview) return;
    setExportingFiltered(true);
    setSaveError(null);
    try {
      const res = await apiFetch("/api/filter-provider-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          listIds: [...selectedListIds],
          criteria: buildCriteria(),
          exportOnly: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSaveError(data.error || "Export failed."); return; }
      const headers = ["NPI", "Provider Name", "Provider Role", "Business Unit", "Department", "Facility", "Internal ID", "Start Date", "Exclusion Type", "Exclusion Reason", "Notes"];
      const rows = (data.providers || []).map((p) => [
        p.npi, p.provider_name || "", p.provider_role || "", p.business_unit || "",
        p.department || "", p.facility || "", p.internal_id || "", p.start_date || "",
        p.exclusionSource || "", p.exclusionReason || "", p.notes || "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(newListName || "filtered_providers").trim().replace(/\s+/g, "_") || "filtered_providers"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSaveError("Network error during export. Please try again.");
    } finally {
      setExportingFiltered(false);
    }
  }

  function reset() {
    setMinPayments("");
    setMaxPayments("");
    setMinOwnership("");
    setMaxOwnership("");
    setMinResearch("");
    setMaxResearch("");
    setLatestYearOnly(false);
    setHasOwnership(null);
    setHasResearch(null);
    setIsExcluded(null);
    setHasDualInterest(null);
    setHasPodRisk(null);
    setHasAnyPayments(null);
    setNewListName("");
    setPreview(null);
    setSaveError(null);
    setSaveSuccess(null);
  }

  function TriToggle({ label, value, onChange }) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-white/50 text-xs">{label}</span>
        <div className="flex items-center bg-navy-700 border border-white/10 rounded-lg overflow-hidden">
          {[
            { v: null, label: "Any" },
            { v: true, label: "Yes" },
            { v: false, label: "No" },
          ].map(({ v, label: l }) => (
            <button
              key={String(v)}
              onClick={() => onChange(v)}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                value === v
                  ? "bg-gold-500/20 text-gold-400"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (saveSuccess) {
    return (
      <div className="card border-green-500/20 bg-green-500/5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-green-400 text-sm font-semibold">✓ Filtered list saved</p>
            <p className="text-white/50 text-xs mt-1">
              "{saveSuccess.listName}" — {saveSuccess.providerCount.toLocaleString()} providers
            </p>
          </div>
          <button onClick={reset} className="text-white/30 hover:text-white/60 text-xs underline">
            Filter again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="text-gold-400/70 text-sm">⚗</span>
          <span className="text-white/70 text-sm font-semibold">Filter This List</span>
          <span className="text-white/30 text-xs">
            Cross-reference {selectedLists.length === 1 ? selectedLists[0].list_name : `${selectedLists.length} selected lists`} with Open Payments data
          </span>
        </div>
        <span className="text-white/30 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="space-y-5 pt-2 border-t border-white/5">
          <p className="text-white/30 text-xs leading-relaxed">
            Set filters below to create a new list containing only providers who match your criteria.
            Compliance officers can use this to narrow a full med staff roster to high-risk providers only.
          </p>

          {/* Latest year scope — applies to all three dollar thresholds below */}
          <div className="flex items-center justify-between bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2.5">
            <div>
              <p className="text-white/70 text-xs font-medium">Latest program year only</p>
              <p className="text-white/30 text-xs mt-0.5">
                Applies to the payment, ownership, and research thresholds below — 2025 only, instead of all years combined. Has no effect on its own — set at least one Min/Max value below for this to change your results.
              </p>
            </div>
            <button
              onClick={() => { setLatestYearOnly(!latestYearOnly); setPreview(null); }}
              className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${latestYearOnly ? "bg-gold-500" : "bg-white/10"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${latestYearOnly ? "translate-x-5" : ""}`} />
            </button>
          </div>

          {/* Payment threshold */}
          <div className="space-y-3">
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest">General Payments</p>
            <TriToggle
              label="Has any general payments"
              value={hasAnyPayments}
              onChange={(v) => { setHasAnyPayments(v); if (v !== true) { setMinPayments(""); setMaxPayments(""); } }}
            />
            {hasAnyPayments !== false && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Min total payments ($)</label>
                  <input
                    type="number"
                    value={minPayments}
                    onChange={(e) => { setMinPayments(e.target.value); setPreview(null); }}
                    placeholder="e.g. 20000"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Max total payments ($)</label>
                  <input
                    type="number"
                    value={maxPayments}
                    onChange={(e) => { setMaxPayments(e.target.value); setPreview(null); }}
                    placeholder="optional"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Ownership threshold */}
          <div className="space-y-3">
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest">Ownership</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-xs mb-1 block">Min ownership value ($)</label>
                <input
                  type="number"
                  value={minOwnership}
                  onChange={(e) => { setMinOwnership(e.target.value); setPreview(null); }}
                  placeholder="e.g. 50000"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
              <div>
                <label className="text-white/40 text-xs mb-1 block">Max ownership value ($)</label>
                <input
                  type="number"
                  value={maxOwnership}
                  onChange={(e) => { setMaxOwnership(e.target.value); setPreview(null); }}
                  placeholder="optional"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>
          </div>

          {/* Research threshold */}
          <div className="space-y-3">
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest">Research Payments</p>
            <p className="text-white/25 text-xs -mt-1">Combines principal investigator and direct research payments</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-xs mb-1 block">Min research payments ($)</label>
                <input
                  type="number"
                  value={minResearch}
                  onChange={(e) => { setMinResearch(e.target.value); setPreview(null); }}
                  placeholder="e.g. 10000"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
              <div>
                <label className="text-white/40 text-xs mb-1 block">Max research payments ($)</label>
                <input
                  type="number"
                  value={maxResearch}
                  onChange={(e) => { setMaxResearch(e.target.value); setPreview(null); }}
                  placeholder="optional"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>
          </div>

          {/* Risk flags */}
          <div className="space-y-3">
            <p className="text-white/50 text-xs font-medium uppercase tracking-widest">Risk Flags</p>
            <TriToggle label="Has ownership interest" value={hasOwnership} onChange={(v) => { setHasOwnership(v); setPreview(null); }} />
            <TriToggle label="Has research payments" value={hasResearch} onChange={(v) => { setHasResearch(v); setPreview(null); }} />
            <TriToggle label="Has dual or triple interest conflict" value={hasDualInterest} onChange={(v) => { setHasDualInterest(v); setPreview(null); }} />
            <TriToggle label="POD risk (OIG Work Plan 2026)" value={hasPodRisk} onChange={(v) => { setHasPodRisk(v); setPreview(null); }} />
            <TriToggle label="OIG / SAM excluded" value={isExcluded} onChange={(v) => { setIsExcluded(v); setPreview(null); }} />
          </div>

          {saveError && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              ✗ {saveError}
            </p>
          )}

          {/* Preview result */}
          {preview && (
            <div className="bg-navy-700/50 border border-white/10 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-white/70 text-sm font-medium">
                  {preview.matchingCount.toLocaleString()} of {preview.totalInList.toLocaleString()} providers match
                </p>
                <span className="text-white/30 text-xs">
                  {preview.totalInList > 0
                    ? `${Math.round((preview.matchingCount / preview.totalInList) * 100)}% of list`
                    : ""}
                </span>
              </div>
              <div className="flex gap-3 flex-wrap">
                {preview.paymentCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-400">
                    💊 {preview.paymentCount.toLocaleString()} with payments
                  </span>
                )}
                {preview.ownershipCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400">
                    🏦 {preview.ownershipCount.toLocaleString()} with ownership
                  </span>
                )}
                {preview.researchCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400">
                    🔬 {preview.researchCount.toLocaleString()} with research
                  </span>
                )}
                {preview.excludedCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">
                    ⚠ {preview.excludedCount.toLocaleString()} excluded
                  </span>
                )}
                {preview.dualInterestCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400">
                    ⚑ {preview.dualInterestCount.toLocaleString()} dual interest
                  </span>
                )}
                {preview.podRiskCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400">
                    ⚠ {preview.podRiskCount.toLocaleString()} POD risk
                  </span>
                )}
                {preview.totalPaymentsSum > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/40">
                    {preview.totalPaymentsSum >= 1_000_000_000
                      ? `$${(preview.totalPaymentsSum / 1_000_000_000).toFixed(2)}B`
                      : `$${(preview.totalPaymentsSum / 1_000_000).toFixed(1)}M`} total payments
                  </span>
                )}
              </div>
              {preview.matchingCount === 0 && (
                <p className="text-white/30 text-xs">No providers match these filters. Try adjusting the criteria.</p>
              )}
            </div>
          )}

          {/* Save section — only shown after preview with results */}
          {preview && preview.matchingCount > 0 && (
            <div className="space-y-2">
              <label className="text-white/50 text-xs mb-1 block">Save as new list</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Name for filtered list"
                  maxLength={60}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
                <button
                  onClick={saveFilteredList}
                  disabled={saving || !newListName.trim()}
                  className="btn-primary text-sm px-5 py-2 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save List"}
                </button>
              </div>
              <p className="text-white/20 text-xs">
                A new list will be created from the {preview.matchingCount.toLocaleString()} matching providers. Your original list is unchanged.
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={runPreview}
              disabled={previewing || !hasAnyCriteria()}
              className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition-all disabled:opacity-40"
            >
              {previewing ? (
                <>
                  <span className="animate-spin text-xs">⏳</span>
                  <span>Checking {selectedLists.reduce((s, l) => s + (l.provider_count || 0), 0).toLocaleString()} providers…</span>
                </>
              ) : (
                "Preview matches"
              )}
            </button>
            {preview && preview.matchingCount > 0 && (
              <button
                onClick={exportFilteredProviders}
                disabled={exportingFiltered}
                className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-all disabled:opacity-40"
              >
                {exportingFiltered ? "Exporting…" : "↓ Export CSV"}
              </button>
            )}
            {hasAnyCriteria() && (
              <button onClick={reset} className="text-white/30 hover:text-white/60 text-sm transition-colors">
                Clear filters
              </button>
            )}
          </div>
          <p className="text-white/20 text-xs">
            Preview queries BigQuery in real time. Large lists (1,000+ providers) may take 5–10 seconds.
          </p>
        </div>
      )}
    </div>
  );
}
const ACCEPTED_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// ── Column aliases — maps common header names → our field names ───────────────
const COL_MAP = {
  npi: ["npi", "npi number", "npi_number", "national provider identifier"],
  provider_name: ["provider name", "provider_name", "name", "physician name", "doctor name"],
  provider_role: ["provider role", "provider_role", "role", "type", "provider type"],
  business_unit: ["business unit", "business_unit", "businessunit", "bu"],
  department: ["department", "dept"],
  facility: ["facility", "location", "site", "clinic", "hospital"],
  internal_id: ["internal id", "internal_id", "employee id", "employee_id", "emp id", "id"],
  start_date: ["start date", "start_date", "hire date", "hire_date", "date joined"],
  status: ["status", "active status"],
  notes: ["notes", "note", "comments"],
};

function normalizeHeaders(row) {
  const normalized = {};
  for (const [key, val] of Object.entries(row)) {
    const lk = key.toLowerCase().trim();
    for (const [field, aliases] of Object.entries(COL_MAP)) {
      if (aliases.includes(lk)) {
        normalized[field] = val;
        break;
      }
    }
    if (!normalized.npi) normalized._raw_key = lk;
  }
  return normalized;
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data.map(normalizeHeaders)),
        error: reject,
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
          resolve(rows.map(normalizeHeaders));
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error("Unsupported file type. Please upload a CSV or Excel file."));
    }
  });
}

// ── Validation report component ───────────────────────────────────────────────
function ValidationReport({ formatErrors, unrecognizedNpis, onConfirm, onCancel, mode }) {
  const [dismissed, setDismissed] = useState(false);
  const hasIssues = formatErrors.length > 0 || unrecognizedNpis.length > 0;

  return (
    <div className="card border border-white/10 mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold text-sm">Validation Report</h3>
        {!hasIssues && (
          <span className="text-green-400 text-xs">✓ All NPIs valid</span>
        )}
      </div>

      {formatErrors.length > 0 && (
        <div>
          <p className="text-red-400 text-xs font-semibold mb-2">
            ✗ {formatErrors.length} NPI format error{formatErrors.length !== 1 ? "s" : ""} — these rows will be skipped
          </p>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {formatErrors.map((e, i) => (
              <div key={i} className="flex gap-3 text-xs text-white/50">
                <span className="text-white/30">Row {e.row}</span>
                <span className="font-mono text-red-400/80">{e.npi}</span>
                <span>{e.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {unrecognizedNpis.length > 0 && !dismissed && (
        <div>
          <p className="text-gold-400 text-xs font-semibold mb-1">
            ⚠ {unrecognizedNpis.length} NPI{unrecognizedNpis.length !== 1 ? "s" : ""} not found in NPPES — provider info may be limited
          </p>
          <p className="text-white/40 text-xs mb-2">
            These NPIs passed format validation but weren't found in our provider database. They may be org NPIs, deactivated, or data entry errors. You can still upload them.
          </p>
          <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1.5 mb-2">
            {unrecognizedNpis.map((npi) => (
              <span key={npi} className="font-mono text-xs px-2 py-0.5 rounded bg-gold-500/10 border border-gold-500/20 text-gold-400">
                {npi}
              </span>
            ))}
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-white/40 hover:text-white/70 text-xs underline"
          >
            Dismiss warnings and proceed
          </button>
        </div>
      )}

      <div className="flex gap-3 pt-2 border-t border-white/10">
        <button onClick={onConfirm} className="btn-primary text-sm px-5 py-2">
          {mode === "delete" ? "Confirm Delete" : mode === "append" ? "Confirm Append" : "Confirm Upload"}
        </button>
        <button onClick={onCancel} className="text-white/40 hover:text-white/70 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Provider list table ───────────────────────────────────────────────────────
function UnmatchedTable({ providers }) {
  if (!providers.length) return null;
  return (
    <div className="card border border-gold-500/20 bg-gold-500/5 space-y-3">
      <div>
        <p className="text-gold-400 text-sm font-semibold">
          ⚠ {providers.length} Unmatched NPI{providers.length !== 1 ? "s" : ""}
        </p>
        <p className="text-white/40 text-xs mt-0.5">
          These providers were not found in NPPES or CMS payment records. Please verify their NPI numbers before relying on this data for compliance purposes.
        </p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gold-500/20">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gold-500/20">
              {["NPI", "Name", "Role", "Facility", "Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-gold-400/60 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providers.map((p, i) => (
              <tr key={p.npi + i} className="border-b border-white/5">
                <td className="px-3 py-2 font-mono text-gold-400/80">{p.npi}</td>
                <td className="px-3 py-2 text-white/60">{p.provider_name || "—"}</td>
                <td className="px-3 py-2 text-white/40">{p.provider_role || "—"}</td>
                <td className="px-3 py-2 text-white/40">{p.facility || "—"}</td>
                <td className="px-3 py-2">
                  
                    <a href={`https://npiregistry.cms.hhs.gov/search?number=${p.npi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400/70 hover:text-gold-400 underline text-xs transition-colors"
                  >
                    Verify on NPPES →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProviderTable({ providers, selectedNpis, onToggleProvider, onToggleAllOnPage }) {
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const pageProviders = providers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(providers.length / PAGE_SIZE);
  const allOnPageSelected = pageProviders.length > 0 && pageProviders.every((p) => selectedNpis.has(p.npi));

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={() => onToggleAllOnPage(pageProviders.map((p) => p.npi), !allOnPageSelected)}
                  className="rounded border-white/20 bg-white/5"
                />
              </th>
              {["NPI", "Name", "Role", "Business Unit", "Department", "Facility", "Status"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-white/40 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageProviders.map((p, i) => (
              <tr key={p.npi + i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedNpis.has(p.npi)}
                    onChange={() => onToggleProvider(p.npi)}
                    className="rounded border-white/20 bg-white/5"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-gold-400/80">
                  <div className="flex items-center gap-1.5">
                    <Link
                      to={`/physician/${p.npi}?name=${encodeURIComponent(p.provider_name || "")}&state=${encodeURIComponent(p.department || "")}`}
                      className="hover:text-gold-400 transition-colors"
                    >
                      {p.npi}
                    </Link>
                    {p.is_unrecognized && (
                      <span
                        title="NPI not found in NPPES — please verify"
                        className="text-gold-400/70 text-xs cursor-help"
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-white/70">
                  <div className="flex items-center gap-1.5">
                    <Link
                      to={`/physician/${p.npi}?name=${encodeURIComponent(p.provider_name || "")}&state=${encodeURIComponent(p.department || "")}`}
                      className="hover:text-gold-400 transition-colors"
                    >
                      {p.provider_name || "—"}
                    </Link>
                    {p.listCount > 1 && (
                      <span
                        title={`In ${p.listCount} of your checked lists`}
                        className="text-xs px-1.5 py-0.5 rounded-full border border-white/15 text-white/40 shrink-0"
                      >
                        {p.listCount} lists
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-white/50">{p.provider_role || "—"}</td>
                <td className="px-3 py-2 text-white/50">{p.business_unit || "—"}</td>
                <td className="px-3 py-2 text-white/50">{p.department || "—"}</td>
                <td className="px-3 py-2 text-white/50">{p.facility || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    !p.status || p.status.toLowerCase() === "active"
                      ? "bg-green-500/10 text-green-400"
                      : p.status.toLowerCase() === "terminated"
                      ? "bg-red-500/10 text-red-400"
                      : "bg-white/10 text-white/40"
                  }`}>
                    {p.status ? p.status.toLowerCase() : "active"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-white/40">
          <span>{providers.length.toLocaleString()} providers</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
            >
              ←
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MyProvidersPage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { isPro, isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const {
    lists, activeList, setActiveList, refreshLists,
    filterActive, toggleFilter, syncing, syncError,
    selectedListIds, selectedListIdsString, toggleListSelection, selectAllLists, deselectAllLists,
    totalSelectedProviders, selectedLists,
  } = useMyProviders();

  const [generatingReport, setGeneratingReport] = useState(false);
  const [showAuditThresholds, setShowAuditThresholds] = useState(false);
  const [reportError, setReportError] = useState(null);

  async function handleGenerateConflictReport() {
    if (selectedListIds.size === 0 || generatingReport) return;

    // Only warn when providers are also individually selected below — that's
    // the specific situation where this button and "Generate Report" on the
    // filtered list genuinely compete for the same intent. Warning on every
    // click, even the ordinary full-list case this button exists for, risks
    // becoming something people click through without reading.
    if (selectedNpis.size > 0) {
      const proceed = window.confirm(
        `You have ${selectedNpis.size} provider${selectedNpis.size !== 1 ? "s" : ""} selected below. This button screens the FULL list(s) checked above — not just your selection.\n\nTo screen only your selected providers, use "Generate Report" on the filtered list instead.\n\nContinue with the full list scan?`
      );
      if (!proceed) return;
    }

    // Must open synchronously, inside the click handler itself — opening a
    // tab after the await below would be blocked as a popup by most
    // browsers, since the click's "user gesture" window expires long before
    // a report this size finishes generating.
    const reportWindow = window.open("", "_blank");
    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating report… — ProviderPayWatch</title>
            <style>
              body { background:#0a1628; color:#e2e8f0; font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; margin:0; }
              .box { text-align:center; }
              .spinner { width:32px; height:32px; border:3px solid rgba(245,158,11,0.2); border-top-color:#f59e0b; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { color:#94a3b8; font-size:14px; }
            </style>
          </head>
          <body>
            <div class="box">
              <div class="spinner"></div>
              <p>Generating your conflict report…<br>This can take up to a few minutes for large lists.</p>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    }

    setGeneratingReport(true);
    setReportError(null);
    try {
      const res = await apiFetch("/api/generate-conflict-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          reportType: "provider_scan",
          listIds: [...selectedListIds],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "failed") {
        setReportError(data.error || "Report generation failed. Please try again.");
        if (reportWindow) reportWindow.close();
        return;
      }
      if (reportWindow) {
        reportWindow.location.href = `/conflict-report/${data.reportId}`;
      } else {
        // Popup blocked despite the synchronous open — fall back to the
        // old same-tab behavior rather than losing the result.
        navigate(`/conflict-report/${data.reportId}`);
      }
    } catch {
      setReportError("Network error. Please try again.");
      if (reportWindow) reportWindow.close();
    } finally {
      setGeneratingReport(false);
    }
  }

  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [mode, setMode] = useState("replace");
  const [listName, setListName] = useState("My Providers");
  const [validationResult, setValidationResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [currentProviders, setCurrentProviders] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [providerPage, setProviderPage] = useState(0);
  const [providerTotal, setProviderTotal] = useState(0);
  const [providerSearch, setProviderSearch] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");
  const PROVIDER_PAGE_SIZE = 100;

  // ── Pooled multi-list selection ───────────────────────────────────────────
  const [selectedNpis, setSelectedNpis] = useState(new Set());
  const [poolNpis, setPoolNpis] = useState(new Set()); // every NPI across checked lists, unsearched — used only to prune selection when a list is unchecked
  const [generatingSelectionReport, setGeneratingSelectionReport] = useState(false);
  const [selectionReportError, setSelectionReportError] = useState(null);

  // Re-fetch lists on mount so newly created filter lists appear immediately
  useEffect(() => {
    if (user) refreshLists();
  }, [user?.id]);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(providerSearch), 300);
    return () => clearTimeout(timer);
  }, [providerSearch]);

  // Auto-load pooled providers whenever which lists are checked, or search, changes
  useEffect(() => {
    if (selectedListIds.size > 0 && user) {
      loadPooledProviders(selectedListIdsString, 0, searchDebounce);
    } else {
      setCurrentProviders([]);
      setProviderTotal(0);
    }
  }, [selectedListIdsString, searchDebounce]);

  // Track every NPI across checked lists (unsearched) so we can prune
  // individually-selected providers the moment a list is unchecked — this
  // must NOT depend on searchDebounce, or clearing a search would wrongly
  // look like providers left the pool.
  useEffect(() => {
    if (selectedListIds.size === 0 || !user) {
      setPoolNpis(new Set());
      setSelectedNpis(new Set());
      return;
    }
    apiFetch(`/api/user-data?action=getProviders&userId=${user.id}&listIds=${selectedListIdsString}&page=0&pageSize=50000`)
      .then((res) => res.json())
      .then((data) => {
        const npis = new Set((data.providers || []).map((p) => p.npi));
        setPoolNpis(npis);
        setSelectedNpis((prev) => new Set([...prev].filter((npi) => npis.has(npi))));
      })
      .catch(() => setPoolNpis(new Set()));
  }, [selectedListIdsString]);

  // ── Gate: must be signed in and Pro ───────────────────────────────────────
  if (!isSignedIn) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">Sign in to manage your provider lists.</p>
        </div>
      </main>
    );
  }

  if (!isPro) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <span className="text-4xl mb-4 block">🔒</span>
          <p className="text-white font-display text-xl mb-2">My Providers</p>
          <p className="text-white/50 text-sm max-w-sm mx-auto mb-6">
            Upload your organization's provider list to monitor conflicts of interest, exclusions, and payment activity across your entire network.
          </p>
          <button
            onClick={() => {
              setUpgradeMessage("Upgrade to Pro to upload and monitor your provider network.");
              setShowUpgradeModal(true);
            }}
            className="btn-primary px-6 py-2.5"
          >
            Upgrade to Pro
          </button>
        </div>
      </main>
    );
  }

  async function loadPooledProviders(listIdsStr, page = 0, search = "") {
    setLoadingProviders(true);
    setProviderPage(page);
    try {
      const searchParam = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";
      const res = await apiFetch(
        `/api/user-data?action=getProviders&userId=${user.id}&listIds=${listIdsStr}&page=${page}&pageSize=${PROVIDER_PAGE_SIZE}${searchParam}`
      );
      const data = await res.json();
      setCurrentProviders(data.providers || []);
      setProviderTotal(data.total || 0);
    } catch {
      setCurrentProviders([]);
      setProviderTotal(0);
    } finally {
      setLoadingProviders(false);
    }
  }

  function toggleProviderSelection(npi) {
    setSelectedNpis((prev) => {
      const next = new Set(prev);
      if (next.has(npi)) next.delete(npi); else next.add(npi);
      return next;
    });
  }

  function toggleAllOnPage(npisArray, checked) {
    setSelectedNpis((prev) => {
      const next = new Set(prev);
      npisArray.forEach((npi) => { if (checked) next.add(npi); else next.delete(npi); });
      return next;
    });
  }

  function deselectAllProviders() {
    setSelectedNpis(new Set());
  }

  async function selectAllMatching() {
    try {
      const searchParam = searchDebounce.trim() ? `&search=${encodeURIComponent(searchDebounce.trim())}` : "";
      const res = await apiFetch(
        `/api/user-data?action=getProviders&userId=${user.id}&listIds=${selectedListIdsString}&page=0&pageSize=50000${searchParam}`
      );
      const data = await res.json();
      const matchingNpis = (data.providers || []).map((p) => p.npi);
      setSelectedNpis((prev) => new Set([...prev, ...matchingNpis]));
    } catch {
      alert("Failed to select all matching providers. Please try again.");
    }
  }

  async function handleGenerateSelectionReport() {
    if (selectedNpis.size === 0 || generatingSelectionReport) return;
    const reportWindow = window.open("", "_blank");
    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating report… — ProviderPayWatch</title>
            <style>
              body { background:#0a1628; color:#e2e8f0; font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; margin:0; }
              .box { text-align:center; }
              .spinner { width:32px; height:32px; border:3px solid rgba(245,158,11,0.2); border-top-color:#f59e0b; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { color:#94a3b8; font-size:14px; }
            </style>
          </head>
          <body>
            <div class="box">
              <div class="spinner"></div>
              <p>Generating your conflict report…<br>This can take a moment for larger selections.</p>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    }
    setGeneratingSelectionReport(true);
    setSelectionReportError(null);
    try {
      const res = await apiFetch("/api/generate-conflict-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          reportType: "provider_scan",
          npis: [...selectedNpis],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "failed") {
        setSelectionReportError(data.error || "Report generation failed. Please try again.");
        if (reportWindow) reportWindow.close();
        return;
      }
      if (reportWindow) {
        reportWindow.location.href = `/conflict-report/${data.reportId}`;
      } else {
        navigate(`/conflict-report/${data.reportId}`);
      }
    } catch {
      setSelectionReportError("Network error. Please try again.");
      if (reportWindow) reportWindow.close();
    } finally {
      setGeneratingSelectionReport(false);
    }
  }

  async function deleteList(list) {
    if (!window.confirm(`Delete "${list.list_name}" and all ${list.provider_count?.toLocaleString()} providers? This cannot be undone.`)) return;
    try {
      await apiFetch("/api/user-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "deleteProviderList", userId: user.id, listId: list.id }),
      });
      if (activeList?.id === list.id) {
        setActiveList(null);
        setCurrentProviders([]);
        setProviderTotal(0);
      }
      refreshLists();
    } catch {
      alert("Failed to delete list. Please try again.");
    }
  }

  // ── File parsing ──────────────────────────────────────────────────────────
  async function handleFile(f) {
    if (!f) return;
    setFile(f);
    setParsedRows([]);
    setParseError(null);
    setValidationResult(null);
    setUploadSuccess(null);

    if (!ACCEPTED_TYPES.includes(f.type) && !f.name.match(/\.(csv|xlsx|xls)$/i)) {
      setParseError("Unsupported file type. Please upload a CSV or .xlsx file.");
      return;
    }

    try {
      const rows = await parseFile(f);
      if (rows.length === 0) {
        setParseError("No data rows found in the file.");
        return;
      }
      if (rows.length > ROW_LIMIT) {
        setParseError(`File has ${rows.length.toLocaleString()} rows — the limit is ${ROW_LIMIT.toLocaleString()}. Please reduce the file size.`);
        return;
      }
      // Check that at least some rows have an NPI column
      const hasNpi = rows.some((r) => r.npi);
      if (!hasNpi) {
        setParseError("No NPI column found. Please ensure your file has a column named 'NPI' or 'NPI Number'.");
        return;
      }
      setParsedRows(rows);
    } catch (err) {
      setParseError(err.message || "Failed to parse file.");
    }
  }

  // ── Preview validation (client-side format check only) ───────────────────
  function previewValidation() {
    if (!listName || !listName.trim()) {
      setParseError("List name is required.");
      return;
    }
    setParseError(null);
    const formatErrors = [];
    const validRows = [];
    parsedRows.forEach((row, i) => {
      const npi = String(row.npi || "").trim();
      if (!npi) {
        formatErrors.push({ row: i + 2, npi: "(empty)", reason: "NPI is empty" });
      } else if (!/^\d{10}$/.test(npi)) {
        formatErrors.push({ row: i + 2, npi, reason: "NPI must be exactly 10 digits" });
      } else {
        validRows.push(row);
      }
    });
    setValidationResult({ formatErrors, unrecognizedNpis: [], validCount: validRows.length, previewOnly: true });
  }

  // ── Submit upload to API ──────────────────────────────────────────────────
  async function submitUpload() {
    setUploading(true);
    try {
      const res = await apiFetch("/api/upload-providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          listName,
          mode,
          providers: parsedRows,
          skipNpisCheck: false,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setParseError(data.error || "Upload failed.");
        setValidationResult(null);
        return;
      }

      // Show full validation report from server
      setValidationResult({
        formatErrors: data.formatErrors || [],
        unrecognizedNpis: data.unrecognizedNpis || [],
        validCount: data.inserted || data.deleted || 0,
        totalInList: data.totalInList,
        serverResult: data,
        previewOnly: false,
      });

      if (data.success) {
        setUploadSuccess(data);
        refreshLists();
        // Reload the active list's provider table — loadProviders was
        // renamed to loadPooledProviders at some point, but this one call
        // site never got updated, so it's been throwing a ReferenceError
        // on every successful upload, silently misreported as "Network
        // error" by the catch block below (the upload itself was always
        // fine — this ran only after it had already succeeded).
        const updatedList = lists.find((l) => l.list_name === listName);
        if (updatedList) loadPooledProviders(updatedList.id, 0, "");
      }
    } catch (err) {
      setParseError("Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  const modeLabel = { replace: "Replace list", append: "Append to list", delete: "Delete from list" };

  async function exportAllProviders() {
    try {
      let all = [];
      let page = 0;
      const pageSize = 1000;
      while (true) {
        const res = await apiFetch(
          `/api/user-data?action=getProviders&userId=${user.id}&listIds=${selectedListIdsString}&page=${page}&pageSize=${pageSize}`
        );
        const data = await res.json();
        all = [...all, ...(data.providers || [])];
        if (all.length >= data.total || (data.providers || []).length === 0) break;
        page++;
      }
      const headers = ["NPI", "Provider Name", "Provider Role", "Business Unit", "Department", "Facility", "Internal ID", "Start Date", "Notes"];
      const rows = all.map((p) => [
        p.npi,
        p.provider_name || "",
        p.provider_role || "",
        p.business_unit || "",
        p.department || "",
        p.facility || "",
        p.internal_id || "",
        p.start_date || "",
        p.notes || "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(activeList?.list_name || "providers").replace(/\s+/g, "_")}_providers.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    }
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10 space-y-8">
      <div className="space-y-4">
        <div>
          <p className="text-gold-400/60 text-xs uppercase tracking-widest mb-1">{isEnterprise ? "Enterprise" : "Pro"}</p>
          <h1 className="font-display text-3xl text-white">My Providers</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedListIds.size >= 2 && selectedListIds.size <= 3 ? (
            <Link
              to={`/compare-lists?listIds=${[...selectedListIds].join(",")}`}
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-all"
            >
              ⇄ Compare {selectedListIds.size} Lists
            </Link>
          ) : (
            <span className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/10 text-white/25 cursor-not-allowed">
              ⇄ Compare Lists
            </span>
          )}
          {!isEnterprise ? (
            <button
              onClick={() => {
                setUpgradeMessage("Generating a conflict report is an Enterprise feature. Upgrade to screen your tracked providers for payment, ownership, and research conflicts.");
                setShowUpgradeModal(true);
              }}
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
            >
              🔒 Generate Conflict Report <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
            </button>
          ) : selectedListIds.size > 0 ? (
            <button
              onClick={handleGenerateConflictReport}
              disabled={generatingReport}
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50"
            >
              {generatingReport
                ? <>⏳ Generating…</>
                : <>🛡 Generate Conflict Report{selectedListIds.size > 1 ? ` (${selectedListIds.size} lists)` : ""}</>}
            </button>
          ) : (
            <span className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/10 text-white/25 cursor-not-allowed">
              🛡 Generate Conflict Report
            </span>
          )}
          <Link
            to="/build-list"
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition-all"
          >
            + Build a List
          </Link>
          {isEnterprise && (
            <Link
              to="/reviewed-findings"
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 transition-all"
            >
              ✓ Reviewed Findings
            </Link>
          )}
          {isEnterprise && (
            <Link
              to="/coi-providers"
              className="md:hidden flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 transition-all"
            >
              📋 COI
            </Link>
          )}
          {isEnterprise && (
            <button
              onClick={() => setShowAuditThresholds(true)}
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-gold-500/20 text-gold-400/70 hover:text-gold-400 hover:border-gold-500/40 transition-all"
            >
              ⚙ Payment Audit Thresholds
            </button>
          )}
          {lists.length > 0 && (
            <div className="flex flex-col items-start gap-1">
              <button
                onClick={toggleFilter}
                disabled={syncing || selectedListIds.size === 0}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border transition-all disabled:opacity-50 ${
                  filterActive
                    ? "bg-gold-500/20 border-gold-500/40 text-gold-400"
                    : "border-white/20 text-white/50 hover:text-white hover:border-white/40"
                }`}
              >
                <span>{syncing ? "⏳" : filterActive ? "◉" : "○"}</span>
                <span>
                  {syncing ? "Syncing..." : filterActive
                    ? `Filtering ${totalSelectedProviders.toLocaleString()} providers`
                    : "Activate Filter"}
                </span>
              </button>
              {syncError && (
                <p className="text-red-400 text-xs">{syncError}</p>
              )}
            </div>
          )}
        </div>
        {showAuditThresholds && (
          <AuditThresholdsPopup
            userId={user?.id}
            fetchFn={apiFetch}
            onClose={() => setShowAuditThresholds(false)}
          />
        )}
      </div>

      {/* ── Existing lists ──────────────────────────────────────────────────── */}
      {lists.length > 0 && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-white/70 text-sm font-semibold">Your Lists</h2>
            {lists.length > 1 && (
              <button
                onClick={selectedListIds.size === lists.length ? deselectAllLists : selectAllLists}
                className="text-white/30 hover:text-white/60 text-xs transition-colors"
              >
                {selectedListIds.size === lists.length ? "Deselect all" : "Select all"}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {lists.map((list) => (
              <div
                key={list.id}
                onClick={() => toggleListSelection(list.id)}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                  selectedListIds.has(list.id)
                    ? "border-gold-500/40 bg-gold-500/5"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                {/* Checkbox — same action as clicking the row, kept for a clear visual target */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleListSelection(list.id); }}
                  className={`flex items-center justify-center w-5 h-5 rounded border mr-3 shrink-0 transition-all ${
                    selectedListIds.has(list.id)
                      ? "bg-gold-500 border-gold-500 text-navy-900"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  {selectedListIds.has(list.id) && <span className="text-xs font-bold">✓</span>}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm truncate">{list.list_name}</p>
                    {!list.bq_synced && (
                      <span className="text-white/30 text-xs shrink-0" title="Not yet synced to filter">○</span>
                    )}
                    {list.bq_synced && (
                      <span className="text-gold-400/40 text-xs shrink-0" title="Ready for filtering">◉</span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs">
                    {list.provider_count?.toLocaleString() || 0} providers · Updated {new Date(list.updated_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-white/20 text-xs">
                    {selectedListIds.has(list.id) ? "✓ in preview" : "click to add"}
                  </span>
                  <Link
                    to={`/my-providers/${list.id}/profile`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-gold-400/50 hover:text-gold-400 text-xs transition-colors whitespace-nowrap"
                  >
                    View Profile →
                  </Link>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteList(list); }}
                    className="text-red-400/40 hover:text-red-400 text-xs transition-colors px-1"
                    title="Delete list"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
          {selectedListIds.size > 0 && (
            <p className="text-white/30 text-xs">
              {selectedListIds.size} list{selectedListIds.size !== 1 ? "s" : ""} selected · {totalSelectedProviders.toLocaleString()} total providers
            </p>
          )}
          {reportError && (
            <p className="text-red-400 text-xs">{reportError}</p>
          )}
        </div>
      )}

      {/* ── Upload form ─────────────────────────────────────────────────────── */}
      <div className="card space-y-5">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-white/70 text-sm font-semibold">Upload Provider List</h2>
          <div className="text-right shrink-0">
            <p className="text-white/30 text-xs">
              {lists.length} of 5 lists used
            </p>
            <p className="text-white/20 text-xs mt-0.5">
              Up to 10,000 providers per list
            </p>
          </div>
        </div>
        {lists.length >= 5 && (
          <div className="bg-gold-500/10 border border-gold-500/20 rounded-lg px-3 py-2.5">
            <p className="text-gold-400 text-xs font-medium">
              ⚠ List limit reached — 5 of 5 lists used
            </p>
            <p className="text-white/40 text-xs mt-0.5">
              Delete an existing list before creating a new one. Uploading to an existing list name will replace or append to it without counting against your limit.
            </p>
          </div>
        )}

        {/* List name */}
        <div>
          <label className="text-white/50 text-xs mb-1 block">List Name</label>
          <input
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            placeholder="My Providers"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
          />
        </div>

        {/* Mode selector */}
        <div>
          <label className="text-white/50 text-xs mb-2 block">Upload Mode</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: "replace", icon: "↺", label: "Replace", desc: "Swap entire list" },
              { value: "append", icon: "+", label: "Append", desc: "Add new NPIs only" },
              { value: "delete", icon: "−", label: "Delete", desc: "Remove listed NPIs" },
            ].map((m) => (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                className={`flex flex-col items-center py-3 px-2 rounded-xl border text-sm transition-all ${
                  mode === m.value
                    ? "border-gold-500/50 bg-gold-500/10 text-gold-400"
                    : "border-white/10 text-white/40 hover:border-white/30 hover:text-white/70"
                }`}
              >
                <span className="text-lg mb-1">{m.icon}</span>
                <span className="font-medium text-xs">{m.label}</span>
                <span className="text-white/30 text-xs mt-0.5">{m.desc}</span>
              </button>
            ))}
          </div>
          {mode === "replace" && (
            <p className="text-gold-400/70 text-xs mt-2">
              ⚠ Replace will permanently overwrite the existing list "{listName}".
            </p>
          )}
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            dragOver
              ? "border-gold-500/60 bg-gold-500/5"
              : file
              ? "border-green-500/30 bg-green-500/5"
              : "border-white/15 hover:border-white/30"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {file ? (
            <div>
              <p className="text-green-400 text-sm font-medium">{file.name}</p>
              <p className="text-white/40 text-xs mt-1">
                {parsedRows.length.toLocaleString()} rows parsed · Click to change file
              </p>
            </div>
          ) : (
            <div>
              <p className="text-4xl mb-3">📂</p>
              <p className="text-white/60 text-sm">Drop your CSV or Excel file here</p>
              <p className="text-white/30 text-xs mt-1">or click to browse · CSV, .xlsx supported · max {ROW_LIMIT.toLocaleString()} rows</p>
            </div>
          )}
        </div>

        {/* Column guide */}
        <details className="text-xs text-white/40">
          <summary className="cursor-pointer hover:text-white/60 transition-colors">
            📋 Expected column names (click to expand)
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-1 pl-2">
            {[
              ["NPI", "Required — 10-digit NPI"],
              ["Provider Name", "Optional"],
              ["Provider Role", "Optional — e.g. Physician, NP"],
              ["Business Unit", "Optional"],
              ["Department", "Optional"],
              ["Facility", "Optional"],
              ["Internal ID", "Optional — your employee/ref ID"],
              ["Start Date", "Optional — YYYY-MM-DD"],
              ["Status", "Optional — active / terminated"],
              ["Notes", "Optional"],
            ].map(([col, desc]) => (
              <div key={col} className="flex gap-2">
                <span className="font-mono text-white/60 min-w-[100px]">{col}</span>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </details>

        {parseError && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            ✗ {parseError}
          </div>
        )}

        {parsedRows.length > 0 && !validationResult && (
          <div className="flex gap-3">
            <button onClick={previewValidation} className="btn-primary text-sm px-5 py-2">
              Validate {parsedRows.length.toLocaleString()} rows
            </button>
            <button onClick={() => { setFile(null); setParsedRows([]); }} className="text-white/40 hover:text-white/70 text-sm">
              Clear
            </button>
          </div>
        )}

        {/* Validation report + confirm */}
        {validationResult && !uploadSuccess && (
          <ValidationReport
            formatErrors={validationResult.formatErrors}
            unrecognizedNpis={validationResult.unrecognizedNpis}
            mode={mode}
            onConfirm={submitUpload}
            onCancel={() => setValidationResult(null)}
          />
        )}

        {uploading && (
          <div className="text-white/50 text-sm animate-pulse">Uploading providers…</div>
        )}

        {uploadSuccess && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 space-y-1">
            <p className="text-green-400 text-sm font-semibold">
              ✓ {mode === "delete" ? "Providers removed" : "Upload complete"}
            </p>
            <p className="text-white/50 text-xs">
              {mode === "delete"
                ? `${uploadSuccess.deleted} provider${uploadSuccess.deleted !== 1 ? "s" : ""} removed · ${(uploadSuccess.totalInList ?? 0).toLocaleString()} remaining in list`
                : `${uploadSuccess.inserted} provider${uploadSuccess.inserted !== 1 ? "s" : ""} ${mode === "append" ? "added" : "uploaded"} · ${uploadSuccess.totalInList?.toLocaleString()} total in list`}
              {uploadSuccess.formatErrors?.length > 0 && ` · ${uploadSuccess.formatErrors.length} rows skipped (format errors)`}
              {uploadSuccess.unrecognizedNpis?.length > 0 && ` · ${uploadSuccess.unrecognizedNpis.length} NPIs not found in NPPES`}
            </p>
            <button
              onClick={() => { setFile(null); setParsedRows([]); setValidationResult(null); setUploadSuccess(null); }}
              className="text-white/40 hover:text-white/60 text-xs underline"
            >
              Upload another file
            </button>
          </div>
        )}
      </div>

      {/* ── Provider table ──────────────────────────────────────────────────── */}
      {selectedListIds.size > 0 && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-white/70 text-sm font-semibold">
              {selectedLists.length} list{selectedLists.length !== 1 ? "s" : ""} selected — {providerTotal.toLocaleString()} providers
            </h2>
            <div className="flex items-center gap-2">
              {selectedLists.length === 1 && (
                <Link
                  to={`/my-providers/${selectedLists[0].id}/profile`}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition-all"
                >
                  📊 View List Profile
                </Link>
              )}
              <button
                onClick={exportAllProviders}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/50 hover:text-white hover:border-white/40 transition-all"
              >
                ↓ Export CSV
              </button>
            </div>
          </div>

          {/* ── Individual provider selection ──────────────────────────────── */}
          <div className="flex items-center justify-between flex-wrap gap-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
            <span className="text-white/50 text-xs">
              {selectedNpis.size} provider{selectedNpis.size !== 1 ? "s" : ""} selected
            </span>
            <div className="flex items-center gap-2">
              <button onClick={selectAllMatching} className="text-xs text-gold-400/70 hover:text-gold-400 transition-colors">
                Select all matching
              </button>
              {selectedNpis.size > 0 && (
                <button onClick={deselectAllProviders} className="text-xs text-white/30 hover:text-white/60 transition-colors">
                  Clear
                </button>
              )}
              <button
                onClick={handleGenerateSelectionReport}
                disabled={selectedNpis.size === 0 || generatingSelectionReport}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-40"
              >
                {generatingSelectionReport ? "⏳ Generating…" : `🛡 Generate Report (${selectedNpis.size})`}
              </button>
            </div>
          </div>
          {selectionReportError && <p className="text-red-400 text-xs">{selectionReportError}</p>}
          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              value={providerSearch}
              onChange={(e) => {
                setProviderSearch(e.target.value);
                setProviderPage(0);
              }}
              placeholder="Search by name, NPI, facility, department, business unit..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50 pr-8"
            />
            {providerSearch && (
              <button
                onClick={() => { setProviderSearch(""); setProviderPage(0); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-xs"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center justify-end">
            {providerTotal > PROVIDER_PAGE_SIZE && (
              <div className="flex items-center gap-2 text-xs text-white/40">
                <button
                  onClick={() => loadPooledProviders(selectedListIdsString, Math.max(0, providerPage - 1), searchDebounce)}
                  disabled={providerPage === 0}
                  className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
                >←</button>
                <span>Page {providerPage + 1} of {Math.ceil(providerTotal / PROVIDER_PAGE_SIZE)}</span>
                <button
                  onClick={() => loadPooledProviders(selectedListIdsString, Math.min(Math.ceil(providerTotal / PROVIDER_PAGE_SIZE) - 1, providerPage + 1), searchDebounce)}
                  disabled={providerPage >= Math.ceil(providerTotal / PROVIDER_PAGE_SIZE) - 1}
                  className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30"
                >→</button>
              </div>
            )}
          </div>
          {loadingProviders ? (
            <div className="text-white/30 text-sm animate-pulse py-6 text-center">Loading providers…</div>
          ) : currentProviders.length > 0 ? (
            <>
              {!providerSearch && <UnmatchedTable providers={currentProviders.filter((p) => p.is_unrecognized)} />}
              <ProviderTable
                providers={currentProviders}
                selectedNpis={selectedNpis}
                onToggleProvider={toggleProviderSelection}
                onToggleAllOnPage={toggleAllOnPage}
              />
            </>
          ) : (
            <p className="text-white/30 text-sm text-center py-6">
              {providerSearch ? `No providers match "${providerSearch}"` : "No providers in this list yet."}
            </p>
          )}
        </div>
      )}

      {/* ── Filter list panel — operates on every checked list, pooled ──────── */}
      {selectedListIds.size > 0 && (
        <FilterListPanel
          selectedListIds={selectedListIds}
          selectedLists={selectedLists}
          userId={user.id}
          onFilterSaved={() => refreshLists()}
          onListLimitReached={() => {}}
        />
      )}
    </main>
  );
}